import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Eye, EyeOff } from 'lucide-react';
import api from '../api';
import AuthLayout from '../components/AuthLayout';

export default function Login() {
  const [form, setForm]       = useState({ correo: '', password: '' });
  const [showPw, setShowPw]   = useState(false);
  const [resetStep, setResetStep] = useState(null); // null | 'email' | 'codigo'
  const [resetForm, setResetForm] = useState({ correo: '', codigo: '', password_nueva: '', password_confirmar: '' });
  const [resetMsg, setResetMsg]   = useState(null);
  const [resetLoading, setResetLoading] = useState(false);
  const [error, setError]     = useState('');
  const [loading, setLoading] = useState(false);
  const navigate  = useNavigate();

  const solicitarReset = async () => {
    if (!resetForm.correo) { setResetMsg({ ok: false, txt: 'Ingresa tu correo' }); return; }
    setResetLoading(true);
    setResetMsg(null);
    try {
      await api.post('/auth/forgot-password', { correo: resetForm.correo });
      setResetMsg({ ok: true, txt: 'Revisa tu correo. Si existe una cuenta, recibirás el código.' });
      setResetStep('codigo');
    } catch(e) {
      setResetMsg({ ok: false, txt: e.response?.data?.detail || 'Error enviando el código' });
    }
    setResetLoading(false);
  };

  const confirmarReset = async () => {
    if (!resetForm.codigo || !resetForm.password_nueva) { setResetMsg({ ok: false, txt: 'Completa todos los campos' }); return; }
    if (resetForm.password_nueva !== resetForm.password_confirmar) { setResetMsg({ ok: false, txt: 'Las contraseñas no coinciden' }); return; }
    if (resetForm.password_nueva.length < 6) { setResetMsg({ ok: false, txt: 'Mínimo 6 caracteres' }); return; }
    setResetLoading(true);
    setResetMsg(null);
    try {
      await api.post('/auth/reset-password', { correo: resetForm.correo, codigo: resetForm.codigo, password_nueva: resetForm.password_nueva });
      setResetMsg({ ok: true, txt: '¡Contraseña actualizada! Ya puedes iniciar sesión.' });
      setTimeout(() => { setResetStep(null); setResetForm({ correo: '', codigo: '', password_nueva: '', password_confirmar: '' }); }, 2000);
    } catch(e) {
      setResetMsg({ ok: false, txt: e.response?.data?.detail || 'Código incorrecto o expirado' });
    }
    setResetLoading(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      const r = await api.post('/auth/login', form);
      localStorage.setItem('token',          r.data.access_token);
      localStorage.setItem('cliente_id',     r.data.cliente_id);
      localStorage.setItem('nombre_empresa', r.data.nombre_empresa);
      localStorage.setItem('es_superadmin',  r.data.es_superadmin ? 'true' : 'false');
      localStorage.setItem('correo',         r.data.correo || '');
      localStorage.setItem('rol',            r.data.rol || 'admin');
      localStorage.setItem('usuario_nombre', r.data.usuario_nombre || '');
      window.location.assign('/');  // recarga: el menú se arma con el rol de quien entró
    } catch (err) {
      const detail = err.response?.data?.detail || '';
      if (detail.startsWith('PENDIENTE_VERIFICACION:')) {
        const clienteId = detail.split(':')[1];
        navigate('/verificar', {
          state: { cliente_id: clienteId, correo: form.correo, nombre_empresa: '' }
        });
      } else {
        setError(detail || 'Correo o contraseña incorrectos');
      }
    } finally { setLoading(false); }
  };

  const inputStyle = {
    width: '100%', padding: '11px 14px', background: 'var(--surface-3)',
    border: '1px solid var(--surface-2)', borderRadius: 10, color: 'var(--text)',
    fontSize: 14, outline: 'none', fontFamily: 'Inter,sans-serif',
    transition: 'border-color .2s, box-shadow .2s',
  };
  const onFocus = e => { e.target.style.borderColor = 'var(--accent-solid)'; e.target.style.boxShadow = '0 0 0 3px var(--accent-soft)'; };
  const onBlur  = e => { e.target.style.borderColor = 'var(--surface-2)'; e.target.style.boxShadow = 'none'; };

  return (
    <AuthLayout>
      {/* Card del formulario — Liquid Glass */}
      <div className="ic-reveal" style={{
        position: 'relative', zIndex: 2,
        background: 'var(--glass)',
        border: '1px solid var(--glass-brd)',
        borderRadius: 22, padding: '38px 40px',
        width: '100%', maxWidth: 420,
        WebkitBackdropFilter: 'blur(28px) saturate(1.6)', backdropFilter: 'blur(28px) saturate(1.6)',
        boxShadow: '0 30px 90px rgba(0,0,0,0.45), inset 0 1px 0 var(--glass-hi)',
        animationDelay: '0.18s',
      }}>
        <h1 style={{ fontFamily: 'Inter,sans-serif', fontSize: 26, fontWeight: 800, color: 'var(--text)', marginBottom: 6, textAlign: 'center' }}>
          Bienvenido de nuevo
        </h1>
        <p style={{ fontSize: 13, color: 'var(--text-3)', marginBottom: 28, textAlign: 'center' }}>
          Inicia sesión para gestionar tus campañas de voz IA
        </p>

        {error && (
          <div style={{
            padding: '11px 14px', background: 'var(--danger-soft)',
            border: '1px solid var(--danger-soft)', borderRadius: 10,
            fontSize: 13, color: 'var(--danger)', marginBottom: 18,
          }}>{error}</div>
        )}

        <form onSubmit={submit}>
          <div style={{ marginBottom: 14 }}>
            <label style={{ fontSize: 11, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '.08em', display: 'block', marginBottom: 7 }}>
              Correo electrónico
            </label>
            <input type="email" value={form.correo}
              onChange={e => setForm({ ...form, correo: e.target.value })}
              required placeholder="tu@empresa.com"
              style={inputStyle} onFocus={onFocus} onBlur={onBlur}
            />
          </div>
          <div style={{ marginBottom: 24 }}>
            <label style={{ fontSize: 11, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '.08em', display: 'block', marginBottom: 7 }}>
              Contraseña
            </label>
            <div style={{ position: 'relative' }}>
              <input type={showPw ? 'text' : 'password'} value={form.password}
                onChange={e => setForm({ ...form, password: e.target.value })}
                required placeholder="••••••••"
                style={{ ...inputStyle, paddingRight: 42 }} onFocus={onFocus} onBlur={onBlur}
              />
              <button type="button" onClick={() => setShowPw(p => !p)} tabIndex={-1}
                aria-label={showPw ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                style={{ position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)', width: 30, height: 30, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'none', border: 'none', color: 'var(--text-3)', cursor: 'pointer', borderRadius: 7 }}>
                {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>
          <button type="submit" disabled={loading} style={{
            width: '100%', padding: '13px', borderRadius: 12, border: 'none',
            background: loading ? 'var(--surface-2)' : 'linear-gradient(135deg,var(--accent-solid),var(--accent-2))',
            color: loading ? 'var(--text-3)' : '#fff',
            fontSize: 15, fontWeight: 700, cursor: loading ? 'not-allowed' : 'pointer',
            boxShadow: loading ? 'none' : '0 6px 24px rgba(124,58,237,0.4)',
            transition: 'transform 0.2s ease, box-shadow 0.2s ease',
          }}
            onMouseEnter={e => { if (!loading) { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 10px 32px rgba(124,58,237,0.55)'; } }}
            onMouseLeave={e => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = loading ? 'none' : '0 6px 24px rgba(124,58,237,0.4)'; }}
          >
            {loading ? 'Ingresando...' : 'Ingresar →'}
          </button>
          <div style={{ textAlign: 'center', marginTop: 14 }}>
            <button onClick={() => { setResetStep('email'); setResetMsg(null); }} type="button"
              style={{ background: 'none', border: 'none', color: 'var(--accent-solid)', fontSize: 13, cursor: 'pointer', textDecoration: 'underline' }}>
              ¿Olvidaste tu contraseña?
            </button>
          </div>
        </form>

        {/* Modal reset contraseña */}
        {resetStep && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)', WebkitBackdropFilter: 'blur(4px)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
            <div className="ic-card ic-reveal" style={{ background: 'var(--surface)', border: '1px solid var(--surface-2)', borderRadius: 16, padding: 32, width: '100%', maxWidth: 420 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--text)' }}>
                  {resetStep === 'email' ? 'Recuperar contraseña' : 'Ingresa el código'}
                </div>
                <button onClick={() => { setResetStep(null); setResetMsg(null); }} type="button"
                  style={{ background: 'none', border: 'none', color: 'var(--text-3)', fontSize: 20, cursor: 'pointer' }}>×</button>
              </div>

              {resetStep === 'email' && (
                <>
                  <p style={{ fontSize: 13, color: 'var(--text-3)', marginBottom: 16 }}>Ingresa tu correo y te enviaremos un código de verificación.</p>
                  <input type="email" placeholder="tu@correo.com" value={resetForm.correo}
                    onChange={e => setResetForm(f => ({ ...f, correo: e.target.value }))}
                    style={{ ...inputStyle, borderRadius: 9, marginBottom: 14 }} onFocus={onFocus} onBlur={onBlur} />
                  <button onClick={solicitarReset} disabled={resetLoading} type="button"
                    style={{ width: '100%', padding: '12px', background: 'linear-gradient(135deg,var(--accent-solid),var(--accent-2))', border: 'none', color: '#fff', borderRadius: 10, fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>
                    {resetLoading ? 'Enviando...' : 'Enviar código'}
                  </button>
                </>
              )}

              {resetStep === 'codigo' && (
                <>
                  <p style={{ fontSize: 13, color: 'var(--text-3)', marginBottom: 16 }}>Revisa tu correo <strong style={{ color: 'var(--accent)' }}>{resetForm.correo}</strong> e ingresa el código de 6 dígitos.</p>
                  {[['codigo','Código de verificación','text'],['password_nueva','Nueva contraseña','password'],['password_confirmar','Confirmar contraseña','password']].map(([key, label, type]) => (
                    <div key={key} style={{ marginBottom: 12 }}>
                      <label style={{ fontSize: 11, color: 'var(--text-3)', display: 'block', marginBottom: 5 }}>{label}</label>
                      <input type={type} value={resetForm[key]} onChange={e => setResetForm(f => ({ ...f, [key]: e.target.value }))}
                        style={{ ...inputStyle, borderRadius: 9 }} onFocus={onFocus} onBlur={onBlur} />
                    </div>
                  ))}
                  <button onClick={confirmarReset} disabled={resetLoading} type="button"
                    style={{ width: '100%', padding: '12px', background: 'linear-gradient(135deg,var(--accent-solid),var(--accent-2))', border: 'none', color: '#fff', borderRadius: 10, fontSize: 14, fontWeight: 700, cursor: 'pointer', marginTop: 6 }}>
                    {resetLoading ? 'Verificando...' : 'Cambiar contraseña'}
                  </button>
                  <button onClick={() => setResetStep('email')} type="button"
                    style={{ width: '100%', padding: '10px', background: 'none', border: 'none', color: 'var(--text-3)', fontSize: 13, cursor: 'pointer', marginTop: 8 }}>
                    ← Volver a ingresar correo
                  </button>
                </>
              )}

              {resetMsg && (
                <div style={{ marginTop: 14, padding: '10px 14px', borderRadius: 8, fontSize: 13, fontWeight: 600,
                  background: resetMsg.ok ? 'var(--success-soft)' : 'var(--danger-soft)',
                  color: resetMsg.ok ? 'var(--success)' : 'var(--danger)' }}>
                  {resetMsg.txt}
                </div>
              )}
            </div>
          </div>
        )}

        <p style={{ textAlign: 'center', marginTop: 20, fontSize: 13, color: 'var(--text-3)' }}>
          ¿No tienes cuenta?{' '}
          <Link to="/register" style={{ color: 'var(--accent-solid)', textDecoration: 'none', fontWeight: 600 }}>
            Regístrate gratis
          </Link>
        </p>
      </div>
    </AuthLayout>
  );
}
